import React from 'react';
import { cssMap, cx } from '@compiled/react';

import { Pressable, Box, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import type { AvatarProps } from '@atlaskit/avatar-group';
import AvatarGroup from '@atlaskit/avatar-group';

const MAX_AVATARS_SHOWN = 2;

const styles = cssMap({
	approvalStatusButton: {
		padding: token('space.200'),
		borderRadius: token('radius.medium'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		color: token('color.text'),
		backgroundColor: token('color.background.neutral.subtle'),

		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	approvalStatusContainer: {
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
	},
	fullWidth: {
		width: '100%',
	},
	chevronContainer: {},
});

export interface ApprovalStatusButtonProps {
	children: React.ReactNode;
	onClick?: () => void;
	avatarProps?: AvatarProps[];
}

export const ApprovalStatusButton = ({
	children,
	onClick,
	avatarProps = [],
}: ApprovalStatusButtonProps): React.JSX.Element => {
	return (
		<Box xcss={cx(styles.fullWidth, styles.approvalStatusContainer)}>
			<Pressable
				xcss={cx(styles.approvalStatusButton, styles.fullWidth)}
				onClick={onClick}
				testId="approval-status-button"
			>
				<Flex
					direction="row"
					xcss={styles.fullWidth}
					justifyContent="space-between"
					alignItems="center"
					gap="space.200"
				>
					{children}
					<Flex gap="space.150" alignItems="center">
						{avatarProps && avatarProps.length > 0 && (
							// @ts-expect-error - `inert` is a valid HTML attribute but not yet in @types/react
							// Needed because AvatarGroup is hardcoded as interactive https://product-fabric.atlassian.net/browse/DSP-20520
							<div inert="true">
								<AvatarGroup
									size="small"
									appearance="stack"
									data={avatarProps}
									maxCount={MAX_AVATARS_SHOWN}
								/>
							</div>
						)}
						<Flex
							alignItems="center"
							justifyContent="center"
							direction="column"
							xcss={styles.chevronContainer}
						>
							<ChevronRightIcon label="" />
						</Flex>
					</Flex>
				</Flex>
			</Pressable>
		</Box>
	);
};
